var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BoxGeometry, InstancedMesh, Line, LineSegments, Mesh, MeshBasicMaterial, Object3D, Points, SphereGeometry, Sprite, SRGBColorSpace, } from "three";
import { WorldObjectType, } from "../api";
import { assetFromEndpoint, CesiumIon3DTilesAsset, CesiumIonKMLAsset } from "../private/data/cesium/assets";
import { fetchTileset } from "../private/data/formats/3d_tiles";
import { TextureLoader, IfcLoader, KmlLoader, ObjLoader, DxfLoader } from "../private/data/loaders";
import { PickingSpriteMaterial } from "../private/materials";
import { LabelCollection } from "../private/objects/labelCollection";
import { Tileset3D } from "../private/objects/tileset_3d";
import { ObjectType } from "../private/picking";
import { FontAsset } from "./FontAsset";
import { IndexedTriangleMesh } from "./IndexedTriangleMesh";
import { LineMesh } from "./LineMesh";
import { LineStripMesh } from "./LineStripMesh";
import { constructLineMaterial, constructMeshThreeMaterial, constructPointsThreeMaterial, IfcObject, MeshObject, ObjObject, PanoramaObject, TilesetObject, } from "./objects";
import { InstancedMeshObject } from "./objects/InstancedMeshObject";
import { KmlObject } from "./objects/KmlObject";
import { LabelCollectionObject } from "./objects/LabelCollectionObject";
import { SpriteObject } from "./objects/SpriteObject";
import { PointMesh } from "./PointMesh";
import { Ifc3D } from "../private/objects/ifc_3d";
import { DxfObject } from "./objects/DxfObject";
/**
 * Implementation of IObjectFactory
 */
export class ObjectFactory {
    constructor(renderer) {
        this.spriteCache = new Map();
        this.renderer = renderer;
    }
    createPanorama(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            const handle = this.renderer.requestManager.queueRequest("priority", () => __awaiter(this, void 0, void 0, function* () {
                const texture = yield TextureLoader.load(desc.url, {
                    headers: {
                        "Cache-Control": "no-cache, no-store",
                    },
                });
                // @ts-ignore
                texture.colorSpace = SRGBColorSpace;
                const material = new MeshBasicMaterial({
                    depthTest: false,
                    depthWrite: false,
                    map: texture,
                });
                const mesh = new Mesh(PanoramaObject.GEOMETRY, material);
                const pano = new PanoramaObject(mesh);
                applyDescRotation(desc, pano);
                return pano;
            }));
            return handle.promise;
        });
    }
    createBox(desc) {
        const box = new BoxGeometry(desc.size.x, desc.size.y, desc.size.z);
        box.computeBoundingSphere();
        box.computeBoundsTree();
        const id = this.renderer.pickingManager.newId();
        const object = new Mesh(box, constructMeshThreeMaterial(desc.material, [], id, false, false));
        const meshObject = new MeshObject(object, WorldObjectType.BoxObject, id);
        this.renderer.pickingManager.setForId(id, {
            objectType: ObjectType.Primitive,
            rootObject: meshObject,
        });
        this.renderer.pickingManager.setIdForObject(object.id, id);
        applyDescPosition(desc, meshObject);
        applyDescRotation(desc, meshObject);
        applyDescScale(desc, meshObject);
        return meshObject;
    }
    createSphere(desc) {
        const sphere = new SphereGeometry(desc.radius, desc.widthSegments, desc.heightSegments);
        sphere.computeBoundingSphere();
        sphere.computeBoundsTree();
        const id = this.renderer.pickingManager.newId();
        const object = new Mesh(sphere, constructMeshThreeMaterial(desc.material, [], id, false, false));
        const meshObject = new MeshObject(object, WorldObjectType.SphereObject, id);
        this.renderer.pickingManager.setForId(id, {
            objectType: ObjectType.Primitive,
            rootObject: meshObject,
        });
        this.renderer.pickingManager.setIdForObject(object.id, id);
        applyDescPosition(desc, meshObject);
        applyDescRotation(desc, meshObject);
        applyDescScale(desc, meshObject);
        return meshObject;
    }
    createRawMeshObject(desc) {
        const id = this.renderer.pickingManager.newId();
        let object;
        let type;
        if (desc.mesh instanceof PointMesh) {
            const userIdChannel = desc.mesh.pickingUserId !== undefined;
            object = new Points(desc.mesh.geometry, constructPointsThreeMaterial(desc.material, [], id, userIdChannel));
            type = WorldObjectType.PointObject;
        }
        else if (desc.mesh instanceof LineMesh) {
            const userIdChannel = desc.mesh.pickingUserId !== undefined;
            object = new LineSegments(desc.mesh.geometry, constructLineMaterial(desc.material, [], id, userIdChannel));
            type = WorldObjectType.LineSegmentObject;
        }
        else if (desc.mesh instanceof LineStripMesh) {
            const userIdChannel = desc.mesh.pickingUserId !== undefined;
            object = new Line(desc.mesh.geometry, constructLineMaterial(desc.material, [], id, userIdChannel));
            type = WorldObjectType.LineObject;
        }
        else if (desc.mesh instanceof IndexedTriangleMesh) {
            const userIdChannel = desc.mesh.pickingUserId !== undefined;
            object = new Mesh(desc.mesh.geometry, constructMeshThreeMaterial(desc.material, [], id, userIdChannel, false));
            type = WorldObjectType.MeshObject;
        }
        else {
            throw new Error("Unknown mesh type");
        }
        const meshObject = new MeshObject(object, type, id, desc.mesh);
        this.renderer.pickingManager.setForId(id, {
            objectType: ObjectType.Primitive,
            rootObject: meshObject,
        });
        this.renderer.pickingManager.setIdForObject(object.id, id);
        applyDescPosition(desc, meshObject);
        applyDescRotation(desc, meshObject);
        applyDescScale(desc, meshObject);
        return meshObject;
    }
    createRawInstancedMeshObject(desc) {
        const id = this.renderer.pickingManager.newId();
        const numInstances = Math.min(desc.positions.length, desc.rotations.length, desc.scales.length);
        let object;
        if (desc.mesh instanceof IndexedTriangleMesh) {
            const material = constructMeshThreeMaterial(desc.material, [], id, false, true);
            object = new InstancedMesh(desc.mesh.geometry, material, numInstances);
        }
        else {
            throw new Error("Unknown/unsupported mesh type");
        }
        const objectData = {
            objectType: ObjectType.Primitive,
            // @ts-ignore
            rootObject: undefined,
        };
        const meshObject = new InstancedMeshObject(object, id, desc.positions, desc.rotations, desc.scales, desc.colors, objectData, this.renderer.pickingManager, desc.mesh);
        objectData.rootObject = meshObject;
        this.renderer.pickingManager.setForId(id, objectData);
        this.renderer.pickingManager.setIdForObject(object.id, id);
        applyDescPosition(desc, meshObject);
        applyDescRotation(desc, meshObject);
        applyDescScale(desc, meshObject);
        return meshObject;
    }
    createTileset(desc) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            let tilesetObject;
            switch (desc.type) {
                case "cesium": {
                    if (this.cesium !== undefined) {
                        const endpoint = yield this.cesium.getAssetEndpoint(desc.assetId);
                        if (!endpoint.isExternal) {
                            const asset = yield assetFromEndpoint(endpoint.endpoint);
                            if (asset instanceof CesiumIon3DTilesAsset) {
                                tilesetObject = new TilesetObject(asset.createTileset3D((_a = desc.style) !== null && _a !== void 0 ? _a : {}, this.renderer.pickingManager, this.renderer.requestManager, desc.maximumScreenSpaceError, desc.tileBudget));
                                tilesetObject.object.owner = tilesetObject;
                                break;
                            }
                            else {
                                throw new Error(`Cesium Ion asset '${desc.assetId}' is not a 3D tiles asset`);
                            }
                        }
                        else {
                            throw new Error(`Cesium Ion asset '${desc.assetId}' is an external asset`);
                        }
                    }
                    else {
                        throw new Error("Cesium Ion not available");
                    }
                }
                case "google": {
                    if (this.google !== undefined) {
                        const tileset = yield fetchTileset(`https://tile.googleapis.com/v1/3dtiles/root.json?key=${this.google.token}`, true);
                        tilesetObject = new TilesetObject(new Tileset3D({
                            tileset,
                            styleDesc: (_b = desc.style) !== null && _b !== void 0 ? _b : {},
                            pickingManager: this.renderer.pickingManager,
                            requestManager: this.renderer.requestManager,
                            maximumScreenSpaceError: desc.maximumScreenSpaceError,
                            tileBudget: desc.tileBudget,
                        }));
                        tilesetObject.object.owner = tilesetObject;
                        break;
                    }
                    else {
                        throw new Error("Google not available");
                    }
                }
                case "direct": {
                    const tileset = yield fetchTileset(desc.url, desc.absolute);
                    tilesetObject = new TilesetObject(new Tileset3D({
                        tileset,
                        styleDesc: (_c = desc.style) !== null && _c !== void 0 ? _c : {},
                        pickingManager: this.renderer.pickingManager,
                        requestManager: this.renderer.requestManager,
                        maximumScreenSpaceError: desc.maximumScreenSpaceError,
                        tileBudget: desc.tileBudget,
                    }));
                    tilesetObject.object.owner = tilesetObject;
                    break;
                }
            }
            return tilesetObject;
        });
    }
    createKml(desc) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(desc.labelFont instanceof FontAsset)) {
                throw new Error("Unknown IFontAsset implementation");
            }
            let object;
            const type = (_a = desc.type) !== null && _a !== void 0 ? _a : "direct";
            switch (type) {
                case "cesium":
                    const cDesc = desc;
                    if (this.cesium === undefined) {
                        throw new Error("Cesium Ion not available");
                    }
                    const endpoint = yield this.cesium.getAssetEndpoint(cDesc.assetId);
                    if (endpoint.isExternal) {
                        throw new Error(`Cesium Ion asset '${cDesc.assetId}' is an external asset`);
                    }
                    const asset = yield assetFromEndpoint(endpoint.endpoint);
                    if (!(asset instanceof CesiumIonKMLAsset)) {
                        throw new Error(`Cesium Ion asset '${cDesc.assetId}' is not a 3D tiles asset`);
                    }
                    object = yield asset.loadKml(desc.labelFont.cache, desc.cacheKey);
                    break;
                case "direct":
                    const dDesc = desc;
                    object = yield KmlLoader.load(dDesc.url, { glyphCache: desc.labelFont.cache, cacheKey: desc.cacheKey });
                    break;
            }
            if (object === undefined) {
                return new KmlObject(new Object3D(), 0);
            }
            else {
                return new KmlObject(object, 0);
            }
        });
    }
    createDxf(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.renderer.pickingManager.newId();
            const object = yield DxfLoader.load(desc.url, {
                objectId: id,
                cacheKey: desc.cacheKey,
            });
            const dxfObject = new DxfObject(object, id);
            this.renderer.pickingManager.setForId(id, {
                objectType: ObjectType.Primitive,
                rootObject: dxfObject,
            });
            this.renderer.pickingManager.setIdForObject(object.id, id);
            return dxfObject;
        });
    }
    createObj(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.renderer.pickingManager.newId();
            const object = yield ObjLoader.load(desc.url, {
                objectId: id,
                cacheKey: desc.cacheKey,
            });
            const objObject = new ObjObject(object, id);
            this.renderer.pickingManager.setForId(id, {
                objectType: ObjectType.Primitive,
                rootObject: objObject,
            });
            this.renderer.pickingManager.setIdForObject(object.id, id);
            return objObject;
        });
    }
    createIfc(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.renderer.pickingManager.newId();
            const { ifcApi, modelId, mesh, rangeGroups } = yield IfcLoader.load(desc.url, {
                objectId: id,
                cacheKey: desc.cacheKey,
            });
            const pickingManager = this.renderer.pickingManager;
            const ifcObject = new IfcObject(new Ifc3D({
                ifcApi,
                mesh,
                rangeGroups,
                modelId,
                pickingManager,
                objectId: id,
            }), id);
            ifcObject.object.owner = ifcObject;
            ifcObject.object.refreshGroups();
            return ifcObject;
        });
    }
    createSprite(desc) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.renderer.pickingManager.newId();
            // Try to fetch the matching sprite cache item
            let cachedTex = this.spriteCache.get(desc.url);
            // No cache item is found, initialize and empty item in the cache
            if (cachedTex === undefined) {
                cachedTex = {};
                this.spriteCache.set(desc.url, cachedTex);
            }
            // The texture has not yet been loaded, so we need to wait for it to be loaded
            if (cachedTex.texture === undefined) {
                // No request has been issued (this is the first time this texture was asked for) so we need to issue one
                // to load the texture
                if (cachedTex.inFlight === undefined) {
                    cachedTex.inFlight = TextureLoader.load(desc.url);
                }
                // Wait for the already in flight request, or the one we just issued
                cachedTex.texture = yield cachedTex.inFlight;
                // @ts-ignore
                cachedTex.texture.colorSpace = SRGBColorSpace;
            }
            const sprite = new Sprite(new PickingSpriteMaterial({
                map: cachedTex.texture,
                sizeAttenuation: desc.sizeAttenuation === undefined ? true : desc.sizeAttenuation,
                alphaTest: 0.5,
                objectId: id,
                extraId: 0,
                transparent: false,
            }));
            const spriteObj = new SpriteObject(sprite, id, desc.pickingUserId);
            this.renderer.pickingManager.setForId(id, {
                objectType: ObjectType.Primitive,
                rootObject: spriteObj,
            });
            this.renderer.pickingManager.setIdForObject(sprite.id, id);
            applyDescPosition(desc, spriteObj);
            applyDescScale(desc, spriteObj);
            if (desc.material !== undefined) {
                spriteObj.applyMaterial(desc.material);
            }
            return spriteObj;
        });
    }
    createLabelCollection(desc) {
        const id = this.renderer.pickingManager.newId();
        if (desc.font instanceof FontAsset) {
            const labelCollection = new LabelCollection(desc.font.cache);
            const labelCollectionObj = new LabelCollectionObject(labelCollection, id);
            applyDescPosition(desc, labelCollectionObj);
            return labelCollectionObj;
        }
        else {
            throw new Error("Unknown IFontAsset implementation");
        }
    }
}
function applyDescPosition(desc, obj) {
    if ("ecef" in desc.position) {
        obj.setPositionECEF(desc.position.ecef);
    }
    else {
        obj.setPositionCartographic(desc.position.cartographic);
    }
}
function applyDescRotation(desc, obj) {
    if (desc.rotation !== undefined) {
        switch (desc.rotation.type) {
            case "explicit":
                obj.setRotation(desc.rotation.quaternion);
                break;
            case "cartographic":
                obj.setRotationCartographic(desc.rotation.cartographic, desc.rotation.heading);
        }
    }
}
function applyDescScale(desc, obj) {
    if (typeof desc.scale !== "undefined") {
        obj.setScale(desc.scale);
    }
}
